import type {
  ClubFound,
  FoundClubsResponse,
  HgFindClubs,
  HgFindClubsReply
} from '@ps/web-shared-api-types/v1-preview/SwHomeGames';
import React from 'react';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import type { ErrorBody } from '@swf/core/errors/RequestError';
import useRequest from '@swf/core/hooks/useRequest';
import { handleServerError, handleServerResponse } from '@swf/poker';

import ENDPOINTS from '../../../constants/endpoints';

interface FormFields {
  ['search-clubs']: string;
}

interface Props {
  setLoading: (loading: boolean) => void;
}

const usePublicClubsRequest = ({ setLoading }: Props) => {
  const request = useRequest();
  const [publicClubs, setPublicClubs] = React.useState<ClubFound[]>([]);
  const [isEmptyResult, setIsEmptyResult] = React.useState(false);

  const handleSubmit = (props: FormFields) => {
    setLoading(true);
    if (!props['search-clubs']) {
      setPublicClubs([]);
      setLoading(false);
      isEmptyResult && setIsEmptyResult(false);
    } else {
      try {
        request({
          service: ENDPOINTS.publicClubs,
          http: true,
          method: 'GET',
          body: {
            findClubs: {
              searchText: props['search-clubs'],
              searchBy: 1
            }
          } as HgFindClubs
        })
          .pipe(catchError((err) => of(err)))
          .subscribe({
            next: ({ body }) => {
              const { error, data } = handleServerResponse<FoundClubsResponse, HgFindClubsReply>({
                response: body,
                accessor: 'findClubsReply'
              });
              if (!error && data.searchResults) {
                setPublicClubs(data.searchResults);

                if (!data.searchResults.length && !isEmptyResult) setIsEmptyResult(true);
              }
              setLoading(false);
            }
          });
      } catch (e) {
        handleServerError(e as ErrorBody);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    }
  };

  return { handleSubmit, publicClubs, isEmptyResult };
};

export default usePublicClubsRequest;
